/* eslint-disable no-console */
import React, { useEffect } from 'react';

import cx from 'classnames';
import { useBedrockRegistration } from 'store';
import {
  SCREENS, SHOW_BACK_BUTTON, BACK_BUTTON, SCREENS_WITH_CIRCLES,
} from 'lib/loginFormConstants';
import { useGeoLocation } from 'lib/Hooks/useGeoLocation';
import { useFeatureFlagContext } from 'lib/ContextTypes';
// Components
import { connect } from 'react-redux';
import { CirclesBackground } from 'components/AccountLoginRegistration/Circles';
import { useBrandText } from 'components/BrandText';
import { ButtonBack } from './Icons/ButtonBack';

// Screens
import { ManualSelect } from './ManualSelect';
import { EmailScreen } from './Email';
import { LogInScreen } from './LogIn';
import { LoginPasswordScreen } from './LoginPassword';
import { SuccessScreen } from './Success';
import { CreateAccountScreen } from './CreateAccount';
import { RegisterScreen } from './Register';
import { SocialRegistrationScreen } from './SocialRegistration';
import { LogInOTCScreen } from './LogInOTC';
import { VerifyOTCScreen } from './VerifyOneTimeCode';
import { UserNameScreen } from './UserName';
import { SelectInterests } from './SelectInterests';
import { PoweredByNBCUProfile } from './PoweredByNBCUProfile';
import { NotAvailableScreen } from './NotAvailable';
import { ChromelessScreen } from './Chromeless';

import styles from './styles.module.scss';

/**
 * Loading component
 * @param {object} props - The props object.
 * @param {boolean} props.loading - The loading state.
 * @returns {React.ReactElement|null} The rendered component.
 */
function Loading({ loading }) {
  if (!loading) return null;
  return (<div data-testid="loading" className={styles.loader} />);
}

/**
 * Loading state after user has completed login/registration
 * @returns {React.ReactElement} The loading component
 */
const LoadingState = () => <Loading loading />;

/**
 * handles the screens for the login and registration flow
 *
 * @param {object} props - Component props.
 * @param {LoginLayout} [props.layout] - The layout for the account, can be 'fixed' or 'fluid'.
 * @param {string} [props.entryTitle] - Title for the entry screen.
 * @param {boolean} [props.showSuccessScreen] - Whether to show a screen for registration success.
 * @param {Function} [props.callback] - Callback function to be called when the login/registration process is done
 * and there are no screens to show.
 * @param {string} [props.backBtn] - The back button label or value.
 * @param {boolean} [props.isOverlay] - To check whether the layout is overlay modal version.
 * @param {string} [props.pageView] - The page type.
 * @param {boolean} [props.isChromeless] - To check whether the layout is Chromeless.
 * @param {string|null} [props.contentType] - The content type that the screen appears in
 * @param {string} [props.className] - Additional className for the wrapper.
 * @param {boolean} [props.isUSRegion] - To check whether the user is in the US region.
 * @returns {React.ReactElement} The rendered component.
 */
const AccountLoginRegistrationCore = ({
  entryTitle,
  layout = 'fixed',
  showSuccessScreen = false,
  callback = () => { },
  backBtn = '',
  isOverlay,
  pageView,
  isChromeless = false,
  contentType = null,
  className = '',
  isUSRegion = true,
}) => {
  const defaultEntryTitle = useBrandText('createAccountHeader');
  const defaultedEntryTitle = entryTitle ?? defaultEntryTitle;

  const loading = useBedrockRegistration((state) => state.loading);
  const screen = useBedrockRegistration((state) => state.screen);
  const setScreen = useBedrockRegistration((state) => state.setScreen);
  const setPageView = useBedrockRegistration((state) => state.setPageView);
  const track = useBedrockRegistration((state) => state.track);
  const error = useBedrockRegistration((state) => state.error);
  const showAuthenticationModal = useBedrockRegistration((state) => state.showAuthenticationModal);

  const launchDarklyFlags = useFeatureFlagContext();
  const newLogin = launchDarklyFlags?.['account-form-updates'];

  // when the overlay version is shown, hide all other versions of the login screen
  const hideScreen = showAuthenticationModal && !isOverlay;

  const { isUsa } = useGeoLocation() ?? {};

  useEffect(() => {
    const capitalizedPageView = pageView.charAt(0).toUpperCase() + pageView.slice(1);
    setPageView(capitalizedPageView);
  }, []);

  useEffect(() => {
    if (error) {
      const payload = {
        action: 'Error',
        method: `${pageView}:${screen}:Error`,
        error,
      };
      track(payload);
    }
  }, [error]);

  /**
   * Returns the appropriate form view based on screen name status
   * @returns {React.Component} The screen component.
   */
  const getScreen = () => {
    // disable login functionality for non-USA users
    if (!isUsa || !isUSRegion) {
      return NotAvailableScreen;
    }

    if (isChromeless) {
      return ChromelessScreen;
    }

    switch (screen) {
      case SCREENS.START:
        if (newLogin) {
          return RegisterScreen;
        }
        return EmailScreen;
      case SCREENS.REGISTER:
        return RegisterScreen;
      case SCREENS.EMAIL:
        return EmailScreen;
      case SCREENS.LOGIN:
        return LoginPasswordScreen;
      case SCREENS.LOGIN_PASSWORD:
        return LogInScreen;
      case SCREENS.CREATE_ACCOUNT:
        return CreateAccountScreen;
      case SCREENS.SOCIAL_REGISTRATION:
        return SocialRegistrationScreen;
      case SCREENS.LOGIN_OTC_EMAIL:
        return LogInOTCScreen;
      case SCREENS.VERIFY_OTC:
      case SCREENS.LOGIN_OTC:
        return VerifyOTCScreen;
      case SCREENS.REGISTRATION_SUCCESS:
        // on account registration success, show the success screen if enabled
        // if the screen is not enabled, set to same setting as login success to dismiss the overlay
        return showSuccessScreen
          ? SuccessScreen
          : setScreen(SCREENS.LOGIN_SUCCESS);
      case SCREENS.LOGIN_SUCCESS:
        return LoadingState;
      case SCREENS.NBCU_PROFILE:
        return PoweredByNBCUProfile;
      case SCREENS.USER_NAME:
        return UserNameScreen;
      case SCREENS.SELECT_INTERESTS:
        return SelectInterests;
      default:
        return EmailScreen;
    }
  };

  const Screen = getScreen();

  /**
   * Handles the back button click event
   * @param {string} targetScreen - The target screen to navigate to
   * @returns {void}
   * */

  /**
   *
   * @param {string} targetScreen - the previous screen
   */
  const handleBackClick = (targetScreen) => {
    const payload = {
      action: 'SignIn:Click',
      value: 'BackButton',
      screen,
      targetScreen,
    };
    track(payload);
    setScreen(targetScreen);
  };

  // Callback when there are no screens to show and the flow is complete
  useEffect(() => {
    if (screen === SCREENS.LOGIN_SUCCESS) callback();
  }, [screen]);

  const screenVal = screen ? `${screen[0].toUpperCase()}${screen.slice(1)}` : 'Start';

  const BackBtn = newLogin ? BACK_BUTTON : SHOW_BACK_BUTTON;
  const renderCircles = SCREENS_WITH_CIRCLES.includes(screen);

  const title = newLogin ? defaultedEntryTitle : 'Sign up or log in with your free TODAY account!';

  return Screen ? (
    <div
      className={cx(styles.wrapper, styles[`wrapper__${layout}`], className)}
      data-testid="screen-wrapper"
      data-activity-map="bedrock-login"
    >
      <ManualSelect screen={screen} setScreen={setScreen} />
      {hideScreen ? null
        : (
          <div className={styles.screen}>
            {BackBtn[screen] ? (
              <ButtonBack
                type={backBtn}
                onClick={() => handleBackClick(BackBtn[screen])}
              />
            ) : null}
            <Loading loading={loading} />
            <Screen
              entryTitle={title.trim() ? title : undefined}
              currentScreen={screenVal}
              pageView={pageView}
              contentType={contentType}
            />
          </div>
        )}
      {renderCircles && <CirclesBackground />}
    </div>
  ) : null;
};

/**
 * Maps the state to props for the AccountLoginRegistration component.
 * @param {object} state - The Redux state.
 * @param {object} state.shared - The shared state.
 * @param {object} state.shared.pageView - The page view object.
 * @param {object} state.shared.vertical - The vertical
 * @returns {object} The mapped props.
 */
function mapStateToProps(state) {
  let { pageView } = state.shared;
  const { isChromeless } = state.shared;
  pageView = `${pageView[0].toUpperCase()}${pageView.slice(1)}`;
  return { pageView, isChromeless };
}

const AccountLoginRegistration = connect(mapStateToProps)(AccountLoginRegistrationCore);
export { AccountLoginRegistration, AccountLoginRegistrationCore };
